import React, { useCallback, useMemo, memo } from 'react';
import { MapMarker } from '@marriott/mi-ui-library';
import { MChatMapMarkerProps } from './MChatMapMarker.types';

/**
 * Memoized MapMarker component to prevent unnecessary re-renders.
 * Only re-renders when:
 * - The marker data changes (propertyId, position, price, etc.)
 * - The active/hovered state for THIS specific marker changes
 *
 * This prevents all markers from re-rendering when only one marker's state changes.
 */
const MChatMapMarkerComponent: React.FC<MChatMapMarkerProps> = ({
  marker,
  clusterer,
  isActive,
  isHovered,
  onMarkerClick,
  onMarkerMouseOver,
  onMarkerMouseOut,
  markerRefs,
  isSinglePropertyResponse,
}) => {
  const hasPrice = !!marker.priceLabel;
  const labelText = hasPrice ? marker.priceLabel! : marker.rateUnavailableLabel || 'N/A';
  const isUnavailable = !hasPrice && marker.rateUnavailableLabel;

  const handleClick = useCallback(() => {
    onMarkerClick(marker);
  }, [onMarkerClick, marker]);

  const handleLoad = useCallback(
    (markerInstance: google.maps.Marker) => {
      markerRefs.current.set(marker.propertyId, markerInstance);

      // The real marker is the hit target; MMapPinHoverOverlay renders a
      // purely visual clone on top. Active-marker z-index is managed
      // imperatively from ChatMapView via markerRefs.
      markerInstance.addListener('mouseover', () => {
        onMarkerMouseOver(marker.propertyId);
      });

      markerInstance.addListener('mouseout', () => {
        onMarkerMouseOut();
      });
    },
    [marker.propertyId, markerRefs, onMarkerMouseOver, onMarkerMouseOut]
  );

  const handleUnmount = useCallback(() => {
    markerRefs.current.delete(marker.propertyId);
  }, [marker.propertyId, markerRefs]);

  const label = useMemo(
    () => ({
      // For single property view the marker is a circle dot, not a text pin.
      // Google Maps requires a non-empty label to create the styleable container,
      // so we use a single space and hide it via the `.m-map-pin-circle` CSS.
      text: isSinglePropertyResponse ? ' ' : labelText,
      className: `m-map-pin ${isSinglePropertyResponse ? 'm-map-pin-circle' : ''} ${
        isActive || isHovered ? 'active' : ''
      } ${isUnavailable && !isActive && !isHovered ? 'marker-label-grey' : ''} ${
        marker.isAIRecommended ? 'ai-recommended' : ''
      }`,
    }),
    [labelText, isActive, isHovered, isUnavailable, marker.isAIRecommended, isSinglePropertyResponse]
  );

  const icon = useMemo(
    () => ({
      url: '',
      scaledSize: new google.maps.Size(75, 20),
    }),
    []
  );

  const position = useMemo(
    () => ({ lat: marker.latitude, lng: marker.longitude }),
    [marker.latitude, marker.longitude]
  );

  // z-index for active markers is set imperatively from ChatMapView via
  // markerRefs - don't pass a zIndex prop here.

  return (
    <MapMarker
      key={marker.propertyId}
      position={position}
      clusterer={clusterer}
      onClick={handleClick}
      onLoad={handleLoad}
      onUnmount={handleUnmount}
      label={label}
      icon={icon}
    />
  );
};

/**
 * Custom comparison function for MChatMapMarker.
 * Only re-render if props that affect the marker's appearance or behavior change.
 */
const areMarkerPropsEqual = (prevProps: MChatMapMarkerProps, nextProps: MChatMapMarkerProps): boolean => {
  if (prevProps.marker.propertyId !== nextProps.marker.propertyId) return false;
  if (prevProps.marker.latitude !== nextProps.marker.latitude) return false;
  if (prevProps.marker.longitude !== nextProps.marker.longitude) return false;
  if (prevProps.marker.priceLabel !== nextProps.marker.priceLabel) return false;
  if (prevProps.marker.rateUnavailableLabel !== nextProps.marker.rateUnavailableLabel) return false;
  if (prevProps.marker.isAIRecommended !== nextProps.marker.isAIRecommended) return false;

  if (prevProps.isActive !== nextProps.isActive) return false;
  if (prevProps.isHovered !== nextProps.isHovered) return false;
  if (prevProps.isSinglePropertyResponse !== nextProps.isSinglePropertyResponse) return false;

  if (prevProps.clusterer !== nextProps.clusterer) return false;

  return true;
};

export const MChatMapMarker = memo(MChatMapMarkerComponent, areMarkerPropsEqual);

export type { MChatMapMarkerProps } from './MChatMapMarker.types';
